import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { isPasswordValid } from "../utils/password";
import { PasswordChecklist } from "../Components/PasswordChecklist/PasswordChecklist";
import { OtpInput } from "../Components/OtpInput/OtpInput";

const API = process.env.REACT_APP_BACKEND_URL;
const headers = { "Content-Type": "application/json" };

// RCS onboarding (opened from the 24h email link):
//   1. Set password with an emailed OTP
//   2. Scan the authenticator QR and verify a code
// After that the user signs in with email + password + authenticator code.
export const Onboarding = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [linkError, setLinkError] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [step, setStep] = useState("password"); // "password" | "totp" | "done"
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  // password step
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // totp step
  const [qrCode, setQrCode] = useState(null);
  const [totpCode, setTotpCode] = useState("");

  const clearMsg = () => { setError(""); setInfo(""); };

  const sendOtp = async (toEmail) => {
    clearMsg();
    try {
      const res = await fetch(`${API}/API/V2/auth/login/forgot-password`, {
        method: "POST", headers, body: JSON.stringify({ email: toEmail }),
      });
      const data = await res.json();
      if (data.code === "C00") setInfo("We've emailed you a 6-digit code to set your password.");
      else setError(data.message || "Failed to send code.");
    } catch { setError("Something went wrong."); }
  };

  const setupTotp = async (forEmail) => {
    clearMsg();
    try {
      const res = await fetch(`${API}/API/V2/auth/rcs/totp/setup`, {
        method: "POST", headers, body: JSON.stringify({ email: forEmail }),
      });
      const data = await res.json();
      if (data.code === "C00") {
        if (data.alreadySetup) setStep("done");
        else setQrCode(data.qrCode);
      } else setError(data.message || "Failed to set up authenticator.");
    } catch { setError("Something went wrong."); }
  };

  useEffect(() => {
    fetch(`${API}/API/V2/auth/rcs/onboard/verify`, {
      method: "POST", headers, body: JSON.stringify({ token }),
    })
      .then((r) => r.json())
      .then((data) => {
        setLoading(false);
        if (data.code !== "C00") {
          setLinkError(data.message || "This link is invalid or has expired.");
          return;
        }
        setEmail(data.email);
        setName(data.name || "");
        if (!data.hasPassword) { setStep("password"); sendOtp(data.email); }
        else if (!data.hasTotp) { setStep("totp"); setupTotp(data.email); }
        else setStep("done");
      })
      .catch(() => {
        setLoading(false);
        setLinkError("Something went wrong. Please try again.");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submitPassword = async (e) => {
    e.preventDefault();
    clearMsg();
    if (newPassword !== confirmPassword) { setError("Passwords do not match."); return; }
    if (!isPasswordValid(newPassword)) { setError("Password does not meet all the requirements below."); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/API/V2/auth/login/reset-password`, {
        method: "POST", headers,
        body: JSON.stringify({ email, otp, password: newPassword, confirmPassword }),
      });
      const data = await res.json();
      if (data.code === "C00") { setStep("totp"); setupTotp(email); }
      else setError(data.message || "Could not set password.");
    } catch { setError("Something went wrong."); }
    setBusy(false);
  };

  const submitTotp = async (e) => {
    e.preventDefault();
    clearMsg();
    setBusy(true);
    try {
      const res = await fetch(`${API}/API/V2/auth/rcs/totp/verify`, {
        method: "POST", headers, body: JSON.stringify({ email, otp: totpCode }),
      });
      const data = await res.json();
      if (data.code === "C00") setStep("done");
      else setError(data.message || "Invalid code.");
    } catch { setError("Something went wrong."); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-jiojbm20 px-4 py-12">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="bg-jiojbm60 px-8 py-5">
          <h1 className="text-white text-xl font-bold tracking-tight">Set up your account</h1>
          <p className="text-jiojbm25 text-xs mt-0.5">
            {name ? `Welcome, ${name}` : "RCS Support"}
          </p>
        </div>

        <div className="px-8 py-8 space-y-6">
          {error && (
            <div className="flex items-start gap-2 px-4 py-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-200">
              <span className="mt-px">⚠</span><span>{error}</span>
            </div>
          )}
          {info && (
            <div className="flex items-start gap-2 px-4 py-3 bg-green-50 text-green-700 text-sm rounded-lg border border-green-200">
              <span className="mt-px">✓</span><span>{info}</span>
            </div>
          )}

          {loading && <p className="text-sm text-gray-500 text-center">Verifying your link…</p>}

          {!loading && linkError && (
            <div className="text-center space-y-4">
              <p className="text-sm text-gray-600">{linkError}</p>
              <button
                onClick={() => navigate("/auth/login")}
                className="w-full bg-jiojbm60 hover:bg-jiojbm70 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm"
              >
                Go to sign in
              </button>
            </div>
          )}

          {/* Step 1: Password */}
          {!loading && !linkError && step === "password" && (
            <form onSubmit={submitPassword} className="space-y-5">
              <p className="text-sm text-gray-600">
                Step 1 of 2 — Set a password for{" "}
                <span className="font-medium text-gray-700">{email}</span>.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Verification Code</label>
                <OtpInput value={otp} onChange={setOtp} autoFocus />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">New Password</label>
                <input
                  type="password" required value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-jiojbm40 focus:border-transparent transition"
                />
                <PasswordChecklist value={newPassword} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm Password</label>
                <input
                  type="password" required value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-jiojbm40 focus:border-transparent transition"
                />
              </div>
              <button
                type="submit"
                disabled={busy || otp.length !== 6 || !isPasswordValid(newPassword) || newPassword !== confirmPassword}
                className="w-full bg-jiojbm60 hover:bg-jiojbm70 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm"
              >
                {busy ? "Saving…" : "Set Password & Continue"}
              </button>
              <button
                type="button" onClick={() => sendOtp(email)}
                className="block w-full text-xs text-jiojbm60 hover:text-jiojbm70 transition-colors"
              >
                Resend code
              </button>
            </form>
          )}

          {/* Step 2: Authenticator */}
          {!loading && !linkError && step === "totp" && (
            <form onSubmit={submitTotp} className="space-y-5">
              <p className="text-sm text-gray-600">
                Step 2 of 2 — Scan this QR code with Google Authenticator, Microsoft
                Authenticator, or any TOTP / Authenticator app, then enter the 6-digit code.
              </p>
              {qrCode && (
                <div className="flex justify-center">
                  <img src={qrCode} alt="Scan to set up authenticator"
                    className="w-48 h-48 rounded-xl border-2 border-gray-200 shadow-sm" />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Authenticator Code</label>
                <OtpInput value={totpCode} onChange={setTotpCode} autoFocus />
              </div>
              <button
                type="submit"
                disabled={busy || totpCode.length !== 6}
                className="w-full bg-jiojbm60 hover:bg-jiojbm70 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm"
              >
                {busy ? "Verifying…" : "Verify & Finish"}
              </button>
            </form>
          )}

          {/* Done */}
          {!loading && !linkError && step === "done" && (
            <div className="text-center space-y-5">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">You're all set</p>
                <p className="text-xs text-gray-500 mt-1">
                  Sign in with your email, password, and authenticator code.
                </p>
              </div>
              <button
                onClick={() => navigate("/auth/login")}
                className="w-full bg-jiojbm60 hover:bg-jiojbm70 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm"
              >
                Go to sign in
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
